import { trackSearchCalendarEventOverlayLegendHover } from 'analytics/snowplow/events/searchCalendarEventOverlayEvents'
import CalendarEventOverlayIcon from 'components/Common/Calendar/CalendarEventOverlayIcon'
import { useCalendarEventTooltip } from 'components/Common/Calendar/CalendarEventTooltipBubble'
import { CALENDAR_EVENT_OVERLAY_FILL } from 'components/Common/Calendar/calendarEventOverlay'
import Group from 'components/utils/Group'
import type { SearchCalendarEvent } from 'constants/searchCalendarEvents'
import useSearchCalendarEventNames from 'hooks/Search/useSearchCalendarEventNames'
import Caption from 'LuxKit/Typography/Caption'
import { rem } from 'polished'
import type { CSSProperties, FocusEvent, MouseEvent } from 'react'
import { useCallback, useId } from 'react'
import styled from 'styled-components'

interface Props {
  events: ReadonlyArray<SearchCalendarEvent>
  placeIds: ReadonlyArray<string>
}

const LegendChip = styled.div`
  display: inline-flex;
  align-items: center;
  column-gap: ${rem(8)};
  padding: ${rem(4)} ${rem(12)};
  border-radius: ${props => props.theme.borderRadius.round};
  background-color: ${CALENDAR_EVENT_OVERLAY_FILL};
  cursor: default;
`

/**
 * Exposes the event description to assistive tech unconditionally.
 *
 * The visual tooltip only exists while hovered or focused, so wiring
 * `aria-describedby` straight at it would leave the description unreachable in
 * browse mode — and this chip is the only place the description is available at
 * all, since the calendar wash is `color-mix` and its icons are decorative.
 */
const HiddenDescription = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  border: 0;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
`

interface LegendItemProps {
  event: SearchCalendarEvent
  label: string
  tooltip: string
  placeIds: ReadonlyArray<string>
}

function SearchCalendarEventLegendItem(props: LegendItemProps) {
  const { event, label, tooltip, placeIds } = props
  const descriptionId = useId()
  const { bubble, hide: hideTooltip, show: showTooltip } = useCalendarEventTooltip(
    tooltip,
    `search-calendar-event-legend-tooltip-${event.id}`,
  )

  const onMouseEnter = useCallback((mouseEvent: MouseEvent<HTMLDivElement>) => {
    showTooltip(mouseEvent.currentTarget)
    trackSearchCalendarEventOverlayLegendHover({ calendarEventId: event.id, placeIds })
  }, [event.id, placeIds, showTooltip])

  const onFocus = useCallback((focusEvent: FocusEvent<HTMLDivElement>) => {
    showTooltip(focusEvent.currentTarget)
  }, [showTooltip])

  return (
    <>
      <LegendChip
        style={{ '--event-overlay-color': event.color } as CSSProperties}
        data-testid={`search-calendar-event-legend-${event.id}`}
        tabIndex={0}
        aria-describedby={descriptionId}
        onMouseEnter={onMouseEnter}
        onMouseLeave={hideTooltip}
        onFocus={onFocus}
        onBlur={hideTooltip}
      >
        <CalendarEventOverlayIcon name={event.icon} size="XS" />
        <Caption variant="medium">
          {label}
        </Caption>
        <HiddenDescription id={descriptionId}>{tooltip}</HiddenDescription>
      </LegendChip>
      {bubble}
    </>
  )
}

function SearchCalendarEventLegend(props: Props) {
  const { events, placeIds } = props
  const copy = useSearchCalendarEventNames(events)

  if (!events.length) return null

  return (
    <Group direction="horizontal" gap={12} verticalAlign="center" wrap="wrap">
      {events.map(event => {
        const item = copy[event.id]
        if (!item) return null

        return (
          <SearchCalendarEventLegendItem
            key={event.id}
            event={event}
            label={item.label}
            tooltip={item.tooltip}
            placeIds={placeIds}
          />
        )
      })}
    </Group>
  )
}

export default SearchCalendarEventLegend
